"use client";

import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { createSlotDateTime, getLocalDateString, formatTime } from "@/lib/format/date";

interface Area {
  id: string;
  name: string;
}

interface Slot {
  id: string;
  areaId: string;
  slotType: string;
  startAt: string;
  endAt: string;
  capacity: number;
  bookedCount: number;
}

interface SlotFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  slot?: Slot | null;
  areas: Area[];
  onSuccess: () => void;
  defaultAreaId?: string;
  defaultDate?: string;
}

const SLOT_TYPES = [
  { value: "MEASUREMENT", label: "Measurement" },
  { value: "PICKUP", label: "Pickup" },
  { value: "DELIVERY", label: "Delivery" },
  { value: "RETURN", label: "Return" },
  { value: "REWORK_PICKUP", label: "Rework Pickup" },
];

export function SlotFormDialog({
  open,
  onOpenChange,
  slot,
  areas,
  onSuccess,
  defaultAreaId,
  defaultDate,
}: SlotFormDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [areaId, setAreaId] = useState("");
  const [slotType, setSlotType] = useState("");
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("10:00");
  const [capacity, setCapacity] = useState("5");

  useEffect(() => {
    if (slot) {
      setAreaId(slot.areaId);
      setSlotType(slot.slotType);
      const startDate = new Date(slot.startAt);
      const endDate = new Date(slot.endAt);
      setDate(getLocalDateString(startDate));
      setStartTime(formatTime(startDate).replace(/\s*(AM|PM)/i, ""));
      setEndTime(formatTime(endDate).replace(/\s*(AM|PM)/i, ""));
      setCapacity(slot.capacity.toString());
    } else {
      setAreaId(defaultAreaId || (areas.length > 0 ? areas[0].id : ""));
      setSlotType("MEASUREMENT");
      setDate(defaultDate || getLocalDateString(new Date()));
      setStartTime("09:00");
      setEndTime("10:00");
      setCapacity("5");
    }
    setError(null);
  }, [slot, open, areas, defaultAreaId, defaultDate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const startAt = createSlotDateTime(date, startTime);
      const endAt = createSlotDateTime(date, endTime);

      if (startAt >= endAt) {
        setError("Start time must be before end time");
        setIsSubmitting(false);
        return;
      }

      const payload = slot
        ? {
            capacity: parseInt(capacity),
          }
        : {
            areaId,
            slotType,
            startAt: startAt.toISOString(),
            endAt: endAt.toISOString(),
            capacity: parseInt(capacity),
          };

      const url = slot
        ? `/api/admin/slots/${slot.id}`
        : "/api/admin/slots";

      const res = await fetch(url, {
        method: slot ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Failed to save slot");
        return;
      }

      onSuccess();
      onOpenChange(false);
    } catch {
      setError("An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle>{slot ? "Edit Slot" : "Create Slot"}</DialogTitle>
          <DialogDescription>
            {slot
              ? "Update slot capacity. Time and type cannot be changed."
              : "Add a new time slot for appointments or logistics."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Area</Label>
            {slot ? (
              <Input
                value={areas.find((a) => a.id === areaId)?.name || ""}
                disabled
                className="bg-slate-50"
              />
            ) : (
              <Select value={areaId} onValueChange={setAreaId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select area" />
                </SelectTrigger>
                <SelectContent>
                  {areas.map((area) => (
                    <SelectItem key={area.id} value={area.id}>
                      {area.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="space-y-2">
            <Label>Slot Type</Label>
            {slot ? (
              <Input
                value={SLOT_TYPES.find((t) => t.value === slotType)?.label || slotType}
                disabled
                className="bg-slate-50"
              />
            ) : (
              <Select value={slotType} onValueChange={setSlotType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {SLOT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="space-y-2">
            <Label>Date</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              disabled={!!slot}
              className={slot ? "bg-slate-50" : ""}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Time</Label>
              <Input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                disabled={!!slot}
                className={slot ? "bg-slate-50" : ""}
              />
            </div>
            <div className="space-y-2">
              <Label>End Time</Label>
              <Input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                disabled={!!slot}
                className={slot ? "bg-slate-50" : ""}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="capacity">Capacity</Label>
            <Input
              id="capacity"
              type="number"
              min="1"
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
            />
            {slot && slot.bookedCount > 0 && (
              <p className="text-xs text-amber-600">
                Currently {slot.bookedCount} booked. Capacity cannot be less than booked count.
              </p>
            )}
          </div>

          {error && (
            <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg border border-red-100">
              {error}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !areaId || !slotType}
              className="bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : slot ? (
                "Update Slot"
              ) : (
                "Create Slot"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
